import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Check, CheckCircle2, CreditCard, Loader2, PlayCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { useTour } from '@/components/tour/TourProvider';
import { MrBubblesFull } from '@/components/tour/MrBubbles';
import { SECTION_TUTORIALS } from '@/lib/tourSteps';
import { useCustomer } from '@/hooks/useCustomer';
import { useStripeMethods } from '@/hooks/useStripeMethods';
import { useCompleteOnboarding } from '@/hooks/useOnboardingStatus';
import { useTourProgress } from '@/hooks/useTour';
import { StripeAddMethodModal } from '@/components/StripeAddMethodModal';
import { StripeCardPurposeModal } from '@/components/StripeCardPurposeModal';

/**
 * /welcome — first-run onboarding for a brand-new customer user. Shown once
 * (gated by customer_users.onboarding_completed_at via the AppShell diversion):
 *   1. Save a payment method — required, EXCEPT accounts on Net credit terms
 *      (billed on terms, no card needed) or with a method already on file.
 *   2. A quick tour of what the portal does, with deep links.
 * Finishing stamps the flag and drops them on /home.
 */
export default function Welcome() {
  const navigate = useNavigate();
  const { data: ctx, isLoading } = useCustomer();
  const complete = useCompleteOnboarding();
  const { startTour, startTutorial } = useTour();
  const { data: tourProgress } = useTourProgress();

  const doneSections = new Set(tourProgress ?? []);
  const totalSections = SECTION_TUTORIALS.length;
  const doneCount = SECTION_TUTORIALS.filter((t) => doneSections.has(t.key)).length;
  const progressPct = totalSections > 0 ? Math.round((doneCount / totalSections) * 100) : 0;

  const methodsQuery = useStripeMethods(!!ctx);
  const methodCount = methodsQuery.data?.methods.length ?? 0;

  const [addOpen, setAddOpen] = useState(false);
  const [purposePmId, setPurposePmId] = useState<string | null>(null);
  // Self-declared "do you rent cylinders/equipment?" — new customers have no
  // rental history to infer from, so we ask.
  const [rentsRentals, setRentsRentals] = useState<'yes' | 'no' | null>(null);

  // Chain accounts bill centrally → exempt from the card-on-file rule (admin
  // toggle). Everyone else keeps a card on file for cylinder/equipment rentals
  // — even on Net terms. They can self-declare no rentals to skip for now.
  const chain = ctx?.customer.cardOnFileExempt === true;
  const terms = ctx?.customer.paymentTerms ?? '';
  const paymentSatisfied = chain || methodCount > 0 || rentsRentals === 'no';

  async function finish() {
    try {
      await complete.mutateAsync();
    } finally {
      navigate('/home', { replace: true });
    }
  }

  if (isLoading || !ctx) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-brix-surface text-brix-muted">
        <Loader2 className="mr-2 animate-spin" size={18} /> Loading…
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-y-auto bg-brix-surface-alt/40 pb-16">
      <div className="mx-auto max-w-2xl px-4 pt-10 md:pt-14">
        {/* Header */}
        <div className="mb-8 text-center">
          <MrBubblesFull size={150} className="mx-auto mb-3 drop-shadow-sm" />
          <h1 className="font-display text-2xl font-bold tracking-tight text-brix-ink md:text-3xl">
            Welcome to Brix Order{ctx.customer.name ? `, ${ctx.customer.name}` : ''}
          </h1>
          <p className="mx-auto mt-2 max-w-lg text-base leading-relaxed text-brix-muted">
            Hi, I'm <span className="font-semibold text-brix-ink">Mr. Bubbles</span> — I'll get you
            set up and show you around. Two quick things and you're ready to order.
          </p>
        </div>

        {/* Step 1 — payment */}
        <section className="mb-5 rounded-2xl border border-brix-border bg-brix-surface p-5 shadow-card md:p-6">
          <div className="flex items-center gap-2">
            <StepDot done={paymentSatisfied} n={1} />
            <h2 className="text-lg font-bold text-brix-ink">Set up how you'll pay</h2>
            {paymentSatisfied && (
              <StatusBadge tone="success" size="sm" icon={<Check size={11} />}>Done</StatusBadge>
            )}
          </div>

          <div className="mt-3 space-y-3 pl-9">
            {chain ? (
              <p className="text-base leading-relaxed text-slate-700">
                Your account bills centrally, so there's <span className="font-semibold">no card
                required</span> here — you're all set.
              </p>
            ) : methodCount > 0 ? (
              <p className="text-base leading-relaxed text-slate-700">
                <span className="font-semibold">Payment method on file ✓</span> — you're all set.
                Manage it anytime under <span className="font-semibold">Payment methods</span>.
              </p>
            ) : (
              <>
                <p className="text-base leading-relaxed text-slate-700">
                  {/\bnet\b/i.test(terms) && (
                    <>
                      You're approved on <span className="font-semibold">{terms}</span> terms — your
                      orders are invoiced.{' '}
                    </>
                  )}
                  We do keep a <span className="font-semibold">card on file for cylinder &amp;
                  equipment rentals</span> (it isn't charged for your regular invoices). Details are
                  encrypted by Stripe and never stored on our servers.
                </p>
                <div className="rounded-xl border border-brix-border bg-brix-surface-alt/50 p-3.5">
                  <p className="text-sm font-semibold text-brix-ink">
                    Do you rent (or plan to rent) CO₂ cylinders or equipment from us?
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button type="button" onClick={() => setRentsRentals('yes')} className={choiceCls(rentsRentals === 'yes')}>
                      Yes, I do
                    </button>
                    <button type="button" onClick={() => setRentsRentals('no')} className={choiceCls(rentsRentals === 'no')}>
                      No / not yet
                    </button>
                  </div>
                </div>
                {rentsRentals === 'yes' && (
                  <div>
                    <p className="text-sm font-semibold text-amber-800">
                      A card on file is required for rentals — add one to continue.
                    </p>
                    <Button className="mt-2" onClick={() => setAddOpen(true)}>
                      <CreditCard size={15} /> Add a payment method
                    </Button>
                  </div>
                )}
                {rentsRentals === 'no' && (
                  <p className="text-sm text-brix-muted">
                    No problem — you can add a card anytime under{' '}
                    <span className="font-semibold">Payment methods</span>. It's required before your
                    first rental.
                  </p>
                )}
              </>
            )}
          </div>
        </section>

        {/* Step 2 — quick tour */}
        <section className="mb-6 rounded-2xl border border-brix-border bg-brix-surface p-5 shadow-card md:p-6">
          <div className="flex items-center gap-2">
            <StepDot done={doneCount === totalSections && totalSections > 0} n={2} />
            <h2 className="text-lg font-bold text-brix-ink">Here's how it works</h2>
            {doneCount > 0 && (
              <StatusBadge tone={doneCount === totalSections ? 'success' : 'info'} size="sm">
                {doneCount} of {totalSections} done
              </StatusBadge>
            )}
          </div>
          <div className="mt-3 pl-0 md:pl-9">
            <button
              type="button"
              onClick={() => startTour()}
              className="inline-flex items-center gap-2 rounded-xl bg-brix-primary px-5 py-3 text-base font-bold text-white transition-colors hover:bg-brix-primary-deep"
            >
              <ArrowRight size={17} /> Take the guided tour
            </button>
            <p className="mt-2 text-base leading-relaxed text-brix-muted">
              A quick walkthrough of the whole portal with me as your guide. Or dive into any area
              below for a step-by-step tutorial — <span className="font-semibold">I'll point out
              every button</span>. Finished areas turn green.
            </p>

            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {SECTION_TUTORIALS.map((t) => {
                const done = doneSections.has(t.key);
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => startTutorial(t.key)}
                    className={`group flex flex-col gap-2 rounded-2xl border p-4 text-left transition-colors ${
                      done
                        ? 'border-emerald-300 bg-emerald-50 hover:bg-emerald-100/70'
                        : 'border-sky-200 bg-sky-50 hover:bg-sky-100/70'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {done ? (
                        <CheckCircle2 size={20} className="shrink-0 text-emerald-600" />
                      ) : (
                        <PlayCircle size={20} className="shrink-0 text-sky-600" />
                      )}
                      <span className="text-base font-bold text-slate-900">{t.label}</span>
                    </div>
                    <p className="text-sm leading-snug text-slate-600">{t.blurb}</p>
                    <span
                      className={`mt-0.5 inline-flex w-fit items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold ${
                        done
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-sky-100 text-sky-700'
                      }`}
                    >
                      {done ? 'Completed' : 'Not started'}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Overall tour progress */}
            <div className="mt-5">
              <div className="mb-1.5 flex items-center justify-between text-sm font-semibold text-brix-ink">
                <span>Tour progress</span>
                <span className="text-brix-muted">
                  {doneCount} / {totalSections} areas
                </span>
              </div>
              <div
                className="h-2.5 w-full overflow-hidden rounded-full bg-brix-surface-alt"
                role="progressbar"
                aria-valuenow={progressPct}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Guided tour progress"
              >
                <div
                  className="h-full rounded-full bg-emerald-500 transition-all duration-500"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Finish */}
        <div className="flex flex-col items-center gap-2">
          <Button
            size="lg"
            className="w-full max-w-sm"
            disabled={!paymentSatisfied || complete.isPending}
            onClick={() => void finish()}
          >
            {complete.isPending ? <Loader2 size={17} className="animate-spin" /> : <ArrowRight size={17} />}
            Go to my dashboard
          </Button>
          {!paymentSatisfied && (
            <p className="text-sm text-brix-muted">Add a payment method above to continue.</p>
          )}
        </div>
      </div>

      {addOpen && (
        <StripeAddMethodModal
          onClose={() => setAddOpen(false)}
          onSaved={(pmId) => {
            setAddOpen(false);
            methodsQuery.refetch();
            // Right after saving: let them default it + turn on rentals, same
            // as Settings → Payment methods.
            setPurposePmId(pmId);
          }}
        />
      )}
      {purposePmId && (
        <StripeCardPurposeModal
          stripePmId={purposePmId}
          autoDefault
          onClose={() => {
            setPurposePmId(null);
            methodsQuery.refetch();
          }}
        />
      )}
    </div>
  );
}

function choiceCls(active: boolean): string {
  return `rounded-lg border px-3.5 py-2 text-sm font-semibold transition-colors ${
    active
      ? 'border-brix-primary bg-brix-primary text-white'
      : 'border-brix-border bg-brix-surface text-brix-ink hover:border-brix-primary/40'
  }`;
}

function StepDot({ n, done }: { n: number; done: boolean }) {
  return (
    <span
      className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
        done ? 'bg-emerald-100 text-emerald-700' : 'bg-brix-primary-tint text-brix-primary'
      }`}
    >
      {done ? <Check size={15} /> : n}
    </span>
  );
}

